import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, Users, Trophy, Clock, Inbox, Trash2 } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_STYLES = {
  new: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  contacted: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  quoted: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  won: 'bg-green-500/20 text-green-300 border-green-500/40',
  lost: 'bg-red-500/20 text-red-300 border-red-500/40',
};
const SOURCE_LABELS = {
  google: 'Google Search', social: 'Facebook / Instagram', referral: 'Referral',
  vehicle: 'Truck / Sign', repeat: 'Returning Customer', other: 'Other',
};
const SERVICE_LABELS = {
  residential: 'Residential', commercial: 'Commercial', hardscape: 'Hardscaping', maintenance: 'Maintenance',
};
const AREA_LABELS = {
  '/areas/west-island': 'West Island',
  '/areas/tmr': 'Town of Mount Royal',
  '/areas/plateau-rosemont': 'Plateau / Rosemont',
  '/areas/south-shore-laval': 'South Shore / Laval',
};
const COLORS = ['#dc2626', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#64748b', '#ec4899'];

const reportedSource = (l) => SOURCE_LABELS[l.heardAboutUs] || 'Not Specified';
const serviceLabel = (l) => SERVICE_LABELS[l.serviceType] || l.serviceType || 'Unknown';
const region = (l) => AREA_LABELS[l.landingPage] || (l.landingPage && l.landingPage !== '/' ? 'Other Page' : 'Home / Direct');
const trackedSource = (l) => {
  if (l.utmSource) return l.utmSource;
  if (l.referrer) {
    try {
      const host = new URL(l.referrer).hostname.replace('www.', '');
      if (host.includes('google')) return 'Google (organic)';
      if (host.includes('facebook') || host.includes('instagram') || host.includes('fb.')) return 'Social (organic)';
      if (host.includes('bing')) return 'Bing (organic)';
      return host;
    } catch (e) { return 'Other'; }
  }
  return 'Direct';
};

const groupBy = (arr, fn) => {
  const m = {};
  arr.forEach(x => { const k = fn(x); m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <Card className="bg-black border-neutral-800" data-testid={testid}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-xl">
        <Icon className="h-6 w-6 text-red-500" />
      </div>
      <div>
        <p className="text-3xl font-bold text-white leading-none">{value}</p>
        <p className="text-sm text-neutral-400 mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card className="bg-black border-neutral-800">
    <CardHeader className="pb-2">
      <CardTitle className="text-base text-neutral-200">{title}</CardTitle>
    </CardHeader>
    <CardContent className="h-[260px]">{children}</CardContent>
  </Card>
);

export default function LeadsDashboard({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/api/quotes`, { headers: authHeader });
      setLeads(res.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const patchLead = async (id, patch) => {
    try {
      await axios.patch(`${API}/api/quotes/${id}`, patch, { headers: authHeader });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
      return true;
    } catch (err) {
      toast.error("Update failed");
      return false;
    }
  };

  const handleStatusChange = async (id, status) => {
    const ok = await patchLead(id, { status });
    if (ok) toast.success(`Marked as ${status}`);
  };

  const openLead = (lead) => {
    setSelected(lead);
    setNoteDraft(lead.notes || "");
  };

  const saveNotes = async () => {
    setSaving(true);
    const ok = await patchLead(selected.id, { notes: noteDraft });
    setSaving(false);
    if (ok) { toast.success("Notes saved"); setSelected(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/api/quotes/${id}`, { headers: authHeader });
      setLeads(prev => prev.filter(l => l.id !== id));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = leads.filter(l => {
      const d = new Date(l.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return {
      total: leads.length,
      newCount: leads.filter(l => !['contacted', 'quoted', 'won', 'lost'].includes(l.status)).length,
      won: leads.filter(l => l.status === 'won').length,
      thisMonth,
    };
  }, [leads]);

  const monthly = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString('en-US', { month: 'short' }), count: 0 });
    }
    leads.forEach(l => {
      const d = new Date(l.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const b = buckets.find(x => x.key === key);
      if (b) b.count++;
    });
    return buckets;
  }, [leads]);

  const bySource = useMemo(() => groupBy(leads, reportedSource), [leads]);
  const byTracked = useMemo(() => groupBy(leads, trackedSource), [leads]);
  const byService = useMemo(() => groupBy(leads, serviceLabel), [leads]);
  const byRegion = useMemo(() => groupBy(leads, region), [leads]);

  const fmtDate = (s) => {
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (e) { return '—'; }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="leads-loading">
        <Loader2 className="animate-spin text-red-600 h-10 w-10" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="leads-dashboard">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Leads" value={stats.total} testid="stat-total" />
        <StatCard icon={Inbox} label="New (unactioned)" value={stats.newCount} testid="stat-new" />
        <StatCard icon={Clock} label="This Month" value={stats.thisMonth} testid="stat-month" />
        <StatCard icon={Trophy} label="Won" value={stats.won} testid="stat-won" />
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-16 text-neutral-500 border border-dashed border-neutral-800 rounded-xl" data-testid="leads-empty">
          No leads yet. Submitted quote requests will appear here.
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            <ChartCard title="Leads Over Time (last 6 months)">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="label" stroke="#737373" fontSize={12} />
                  <YAxis stroke="#737373" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 8, color: '#fff' }} />
                  <Line type="monotone" dataKey="count" stroke="#dc2626" strokeWidth={3} dot={{ fill: '#dc2626' }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Reported Source (How did you hear about us?)">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={bySource} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={11}>
                    {bySource.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 8, color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Tracked Source (ads / referral links)">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byTracked} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={11}>
                    {byTracked.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 8, color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Leads by Service Type">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byService} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="name" stroke="#737373" fontSize={11} />
                  <YAxis stroke="#737373" fontSize={12} allowDecimals={false} />
                  <Tooltip cursor={{ fill: '#ffffff10' }} contentStyle={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 8, color: '#fff' }} />
                  <Bar dataKey="value" fill="#dc2626" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Leads by Service Area">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byRegion} layout="vertical" margin={{ top: 10, right: 20, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis type="number" stroke="#737373" fontSize={12} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#737373" fontSize={11} width={110} />
                  <Tooltip cursor={{ fill: '#ffffff10' }} contentStyle={{ background: '#0a0a0a', border: '1px solid #262626', borderRadius: 8, color: '#fff' }} />
                  <Bar dataKey="value" fill="#f97316" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Leads table */}
          <Card className="bg-black border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white">All Leads ({leads.length})</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-neutral-500 border-b border-neutral-800">
                    <th className="py-3 pr-4 font-medium">Name</th>
                    <th className="py-3 pr-4 font-medium">Contact</th>
                    <th className="py-3 pr-4 font-medium">Service</th>
                    <th className="py-3 pr-4 font-medium">Source</th>
                    <th className="py-3 pr-4 font-medium">Area</th>
                    <th className="py-3 pr-4 font-medium">Date</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                    <th className="py-3 pr-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} className="border-b border-neutral-900 hover:bg-neutral-900/50" data-testid={`lead-row-${l.id}`}>
                      <td className="py-3 pr-4 font-medium text-white whitespace-nowrap">{l.name}</td>
                      <td className="py-3 pr-4 text-neutral-400">
                        <div className="whitespace-nowrap">{l.phone}</div>
                        <div className="text-xs text-neutral-500 truncate max-w-[160px]">{l.email}</div>
                      </td>
                      <td className="py-3 pr-4 text-neutral-300 whitespace-nowrap">{serviceLabel(l)}</td>
                      <td className="py-3 pr-4 text-neutral-300 whitespace-nowrap">{reportedSource(l)}</td>
                      <td className="py-3 pr-4 text-neutral-400 whitespace-nowrap">{region(l)}</td>
                      <td className="py-3 pr-4 text-neutral-400 whitespace-nowrap">{fmtDate(l.createdAt)}</td>
                      <td className="py-3 pr-4">
                        <select
                          data-testid={`lead-status-${l.id}`}
                          value={STATUS_OPTIONS.includes(l.status) ? l.status : 'new'}
                          onChange={e => handleStatusChange(l.id, e.target.value)}
                          className={`capitalize rounded-full border px-3 py-1 text-xs font-semibold bg-transparent outline-none ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s} className="bg-neutral-900 text-white">{s}</option>)}
                        </select>
                      </td>
                      <td className="py-3 pr-4 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openLead(l)} data-testid={`lead-view-${l.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-900/20">
                          View
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(l.id)} className="text-neutral-500 hover:text-red-400 hover:bg-red-900/20">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Lead detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg" data-testid="lead-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selected.name}</DialogTitle>
                <DialogDescription className="text-neutral-500">Lead details and follow-up notes</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Phone" value={selected.phone} />
                <Row label="Email" value={selected.email} />
                <Row label="Service" value={serviceLabel(selected)} />
                <Row label="Reported source" value={reportedSource(selected)} />
                <Row label="Tracked source" value={trackedSource(selected)} />
                <Row label="Campaign" value={selected.utmCampaign || '—'} />
                <Row label="Area / Landing page" value={`${region(selected)} (${selected.landingPage || '/'})`} />
                <Row label="Submitted" value={fmtDate(selected.createdAt)} />
                {selected.message && (
                  <div>
                    <p className="text-neutral-500 mb-1">Project details</p>
                    <p className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-neutral-200 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
                <div>
                  <p className="text-neutral-500 mb-1">Private notes</p>
                  <Textarea
                    data-testid="lead-notes-textarea"
                    value={noteDraft}
                    onChange={e => setNoteDraft(e.target.value)}
                    placeholder="Add follow-up notes, quoted price, next steps..."
                    className="bg-neutral-900 border-neutral-800 min-h-[90px]"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSelected(null)} className="text-neutral-400">Close</Button>
                <Button onClick={saveNotes} disabled={saving} data-testid="lead-save-notes-btn" className="bg-red-600 hover:bg-red-700">
                  {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Save Notes"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-neutral-900 pb-2">
    <span className="text-neutral-500">{label}</span>
    <span className="text-neutral-200 text-right break-all">{value}</span>
  </div>
);
